import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import fs from 'fs';
import path from 'path';
import { jsonCoerce, numCoerce } from '../coerce.js';

/**
 * Register ISR configuration MCP tools.
 * Tools for configuring Incremental Static Regeneration for API-backed collections.
 */
export function registerIsrTools(server: McpServer): void {
  // Tool: Configure ISR for a collection
  server.tool(
    'stackwright_pro_configure_isr',
    'Configure Incremental Static Regeneration (ISR) for an API-backed collection. ' +
      'ISR allows API data to be cached and refreshed on a schedule, providing real-time data ' +
      'with the performance of static generation. ' +
      'Use this after stackwright_pro_generate_filter to set revalidation intervals.',
    {
      collection: z.string().describe('Collection name (e.g., "equipment", "supplies")'),
      revalidateSeconds: numCoerce(z.number().optional()).describe(
        'Revalidation interval in seconds (default: 60)'
      ),
      fallback: z
        .enum(['blocking', 'true', 'false'])
        .optional()
        .describe('Fallback behavior for new pages'),
      configPath: z.string().optional().describe('Path to stackwright.yml'),
    },
    async ({ collection, revalidateSeconds = 60, fallback = 'blocking', configPath }) => {
      const revalidate = revalidateSeconds;

      const yamlSnippet = `# Add to stackwright.yml under integrations:
  - type: openapi
    name: ${collection}
    # ... other config
    isr:
      revalidate: ${revalidate}
      fallback: ${fallback}`;

      let description: string;
      if (revalidate < 60) {
        description = '⚡ Very fresh data (revalidate every ' + revalidate + 's)';
      } else if (revalidate < 300) {
        description = '📊 Fresh data (revalidate every ' + (revalidate / 60).toFixed(0) + ' min)';
      } else if (revalidate < 3600) {
        description = '💾 Cached data (revalidate every ' + (revalidate / 60).toFixed(0) + ' min)';
      } else {
        description =
          '🗄️ Static data (revalidate every ' + (revalidate / 3600).toFixed(0) + ' hour)';
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: `⚙️ ISR Configuration for "${collection}":\n\n${description}\n\nFallback: ${fallback === 'blocking' ? '⏳ Show loading until cached' : fallback === 'true' ? '📄 Generate on demand' : '❌ 404 for new pages'}\n\n\`\`\`yaml\n${yamlSnippet}\n\`\`\``,
          },
        ],
      };
    }
  );

  // Tool: Configure ISR for multiple collections
  server.tool(
    'stackwright_pro_configure_isr_batch',
    'Configure ISR for multiple collections at once. ' +
      'More efficient than calling stackwright_pro_configure_isr multiple times. ' +
      'Provide different revalidation intervals based on data freshness requirements.',
    {
      collections: jsonCoerce(
        z.array(
          z.object({
            name: z.string().describe('Collection name'),
            revalidateSeconds: numCoerce(z.number().optional()).describe('Revalidation interval'),
          })
        )
      ).describe('Array of collection configurations'),
      defaultFallback: z
        .enum(['blocking', 'true', 'false'])
        .optional()
        .describe('Default fallback behavior'),
      configPath: z.string().optional().describe('Path to stackwright.yml'),
    },
    async ({ collections, defaultFallback = 'blocking', configPath }) => {
      const lines = [`⚙️ Batch ISR Configuration:\n`];

      for (const col of collections) {
        const rev = col.revalidateSeconds || 60;
        let freshness = '💾';
        if (rev < 60) freshness = '⚡';
        else if (rev < 300) freshness = '📊';
        else if (rev >= 3600) freshness = '🗄️';

        lines.push(`${freshness} ${col.name}: revalidate=${rev}s`);
      }

      lines.push(`\nFallback: ${defaultFallback}`);

      return {
        content: [
          {
            type: 'text' as const,
            text: lines.join('\n'),
          },
        ],
      };
    }
  );
}
